import { useState } from "react";
import { Star, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FeedbackForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    rating: 5,
    message: "",
    service_type: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (rating) => {
    setFormData((prev) => ({ ...prev, rating }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        rating: 5,
        message: "",
        service_type: "",
      });
    } catch (e) {
      console.error("Feedback submission failed:", e);
    }
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <section
        id="feedback"
        data-testid="feedback-section"
        className="py-20 md:py-32 bg-secondary/30"
      >
        <div className="max-w-xl mx-auto px-6 md:px-12 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Star className="h-8 w-8 text-primary fill-primary" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
          <p className="text-muted-foreground mb-6">
            Your feedback has been submitted and will be reviewed shortly.
          </p>
          <Button
            data-testid="feedback-another-btn"
            onClick={() => setSubmitted(false)}
            variant="outline"
          >
            Submit Another Review
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section
      id="feedback"
      data-testid="feedback-section"
      className="py-20 md:py-32 bg-secondary/30"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-6">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary">
                Your Voice Matters
              </span>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
                Share Your Experience
              </h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Your feedback helps me improve and helps others make informed decisions 
              about their coaching journey. I'd love to hear about your experience.
            </p>
            <div className="p-6 bg-background rounded-xl border border-border/50">
              <p className="italic text-muted-foreground">
                "Your testimonial could help another professional take the first 
                step toward transforming their leadership journey."
              </p>
            </div>
          </div>

          {/* Form */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle>Leave a Review</CardTitle>
              <CardDescription>
                All fields marked with * are required
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="feedback-name">Your Name *</Label>
                  <Input
                    id="feedback-name"
                    name="name"
                    data-testid="feedback-name-input"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="feedback-email">Email Address *</Label>
                  <Input
                    id="feedback-email"
                    name="email"
                    type="email"
                    data-testid="feedback-email-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Service Type</Label>
                  <Select onValueChange={handleServiceChange} value={formData.service_type}>
                    <SelectTrigger data-testid="feedback-service-select">
                      <SelectValue placeholder="Select the service you received" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-on-1">1-on-1 Leadership Coaching</SelectItem>
                      <SelectItem value="cohort">Cohort-Based Group Coaching</SelectItem>
                      <SelectItem value="group">Group Coaching Sessions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Your Rating *</Label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        data-testid={`rating-star-${star}`}
                        onClick={() => handleRatingChange(star)}
                        className="star p-1 rounded hover:bg-muted/50 transition-colors"
                      >
                        <Star
                          className={`h-6 w-6 ${
                            star <= formData.rating
                              ? "fill-accent text-accent"
                              : "text-muted-foreground"
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="feedback-message">Your Review *</Label>
                  <Textarea
                    id="feedback-message"
                    name="message"
                    data-testid="feedback-message-input"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Share your experience and how coaching has helped you..."
                    rows={4}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="feedback-submit-btn"
                  disabled={isSubmitting}
                  className="w-full btn-primary"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner w-4 h-4" />
                      Submitting...
                    </span>
                  ) : (
                    <>
                      Submit Review
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default FeedbackForm;
